import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Modal,
  Form,
  Table,
  Row,
  Col,
  Pagination,
  Container,
} from "react-bootstrap";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import nodeApi from "../lib/api";
import { toast } from "sonner";

export default function Coater() {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    number: "",
    email: "",
    address: "",
  });
  const [editId, setEditId] = useState(null);
  const [filteredCoaters, setFilteredCoaters] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination settings - 10 coaters per page
  const coatersPerPage = 10;

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateNumber = (number) => /^[0-9]{10}$/.test(number);

  const coaterQuery = useQuery({
    queryKey: ["coaters"],
    queryFn: () => nodeApi.get("/coater").then((res) => res.data),
    onError: (err) => {
      toast.error(`Failed to fetch coaters: ${err.message}`);
    },
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      const cleanedData = { name: data.name.trim() };

      if (editId) {
        cleanedData.number = data.number?.trim() || "";
        cleanedData.email = data.email?.trim().toLowerCase() || "";
        cleanedData.address = data.address?.trim() || "";
        return nodeApi.put(`/coater/${editId}`, cleanedData);
      } else {
        if (data.number?.trim()) cleanedData.number = data.number.trim();
        if (data.email?.trim())
          cleanedData.email = data.email.trim().toLowerCase();
        if (data.address?.trim()) cleanedData.address = data.address.trim();

        const lastIdNum =
          coaterQuery.data && coaterQuery.data.length > 0
            ? Math.max(
                ...coaterQuery.data.map((s) =>
                  parseInt(s?.coater_id?.replace(/\D/g, "") || "0", 10)
                )
              )
            : 0;
        const newId = `C${String(lastIdNum + 1).padStart(3, "0")}`;
        return nodeApi.post("/coater", { ...cleanedData, coater_id: newId });
      }
    },
    onSuccess: () => {
      toast.success(`Coater ${editId ? "updated" : "added"} successfully`);
      queryClient.invalidateQueries({ queryKey: ["coaters"] });
      handleClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Error saving coater");
    },
  });

  useEffect(() => {
    if (coaterQuery.isSuccess && coaterQuery.data) {
      let sorted = [...coaterQuery.data].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        if (isNaN(dateA)) return 1;
        if (isNaN(dateB)) return -1;
        return dateB - dateA;
      });

      if (searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        sorted = sorted.filter(
          (c) =>
            c.coater_id?.toLowerCase().includes(term) ||
            c.name?.toLowerCase().includes(term) ||
            c.number?.toLowerCase().includes(term) ||
            c.email?.toLowerCase().includes(term) ||
            c.address?.toLowerCase().includes(term)
        );
      }

      setFilteredCoaters(sorted);
      setCurrentPage(1);
    } else {
      setFilteredCoaters([]);
    }
  }, [searchTerm, coaterQuery.data, coaterQuery.isSuccess]);

  const indexOfLast = currentPage * coatersPerPage;
  const indexOfFirst = indexOfLast - coatersPerPage;
  const currentCoaters = filteredCoaters.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCoaters.length / coatersPerPage);

  let pages = [];
  if (totalPages > 1) {
    pages.push(1);
    if (currentPage > 3) pages.push("dots-left");
    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p > 1 && p < totalPages) pages.push(p);
    }
    if (currentPage < totalPages - 2) pages.push("dots-right");
    pages.push(totalPages);
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "number" && !/^\d*$/.test(value)) return;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Coater Name is required.");
      return;
    }
    if (form.email.trim() && !validateEmail(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (form.number.trim() && !validateNumber(form.number)) {
      toast.error("Please enter a valid contact number (10 digits).");
      return;
    }
    mutation.mutate(form);
  };

  const handleEdit = (c) => {
    setForm({
      name: c.name || "",
      number: c.number || "",
      email: c.email || "",
      address: c.address || "",
    });
    setEditId(c.id);
    setShowModal(true);
  };

  const handleClose = () => {
    setForm({ name: "", number: "", email: "", address: "" });
    setEditId(null);
    setShowModal(false);
  };

  return (
    <div className="d-flex flex-column vh-100">
      <Navbar />
      <div className="d-flex flex-grow-1 overflow-hidden">
        <Sidebar />
        <Container
          fluid
          className="flex-grow-1 d-flex flex-column p-4 overflow-hidden"
        >
          {/* Header */}
          <Row className="mb-4 align-items-center">
            <Col>
              <h4 className="fw-bold m-0">Coaters</h4>
            </Col>
          </Row>

          {/* Search */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search Coaters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col className="text-end">
              <Button variant="primary" onClick={() => setShowModal(true)}>
                Add Coater
              </Button>
            </Col>
          </Row>
          <style>
            {`
            .col-coater { width: 100px; }    
            .col-name { width: 180px; }       
            .col-number { width: 140px; }     
            .col-email { width: 200px; }      
            .col-address { width: 250px; }    
            .col-actions { width: 150px; }`}
          </style>

          {/* Table */}
          <div className="table-responsive flex-grow-1 overflow-auto">
            <Table bordered hover className="mb-0">
              <thead className="table-secondary sticky-top">
                <tr>
                   <th className="col-coater">Coater ID</th>
                  <th className="col-name">Name</th>
                  <th className="col-number">Number</th>
                  <th className="col-email">Email</th>
                  <th className="col-address">Address</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coaterQuery.isLoading && (
                  <tr>
                    <td colSpan="6" className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {coaterQuery.isError && (
                  <tr>
                    <td colSpan="6" className="text-center text-danger">
                      Error fetching data.
                    </td>
                  </tr>
                )}
                {coaterQuery.isSuccess && currentCoaters.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center">
                      {searchTerm
                        ? "No matching coaters found."
                        : "No coaters found."}
                    </td>
                  </tr>
                )}
                {coaterQuery.isSuccess &&
                  currentCoaters.map((c) => (
                    <tr key={c.id}>
                      <td>{c.coater_id || "-"}</td>
                      <td>{c.name || "-"}</td>
                      <td>{c.number || "-"}</td>
                      <td>{c.email || "-"}</td>
                      <td>{c.address || "-"}</td>
                      <td>
                        <Button
                          variant="dark"
                          size="sm"
                          onClick={() => handleEdit(c)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="bg-white py-2"
              style={{ position: "sticky", bottom: 0, zIndex: 10 }}
            >
              <nav>
                <ul className="pagination justify-content-center flex-wrap mb-0">
                  <Pagination.Prev
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Prev
                  </Pagination.Prev>
                  {pages.map((p, idx) => (
                    <li
                      key={idx}
                      className={`page-item ${
                        p === currentPage ? "active" : ""
                      } ${
                        p === "dots-left" || p === "dots-right"
                          ? "disabled"
                          : ""
                      }`}
                    >
                      {p === "dots-left" || p === "dots-right" ? (
                        <span className="page-link">…</span>
                      ) : (
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                      )}
                    </li>
                  ))}
                  <Pagination.Next
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Pagination.Next>
                </ul>
              </nav>
            </div>
          )}

          {/* Modal */}
          <Modal show={showModal} onHide={handleClose} centered>
            <Modal.Header closeButton>
              <Modal.Title>{editId ? "Edit Coater" : "Add Coater"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Coater Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="number"
                    value={form.number}
                    onChange={handleChange}
                    onBlur={() => {
                      if (form.number.trim() && !validateNumber(form.number)) {
                        toast.error(
                          "Invalid contact number. Only 10 digits allowed."
                        );
                      }
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={() => {
                      if (form.email.trim() && !validateEmail(form.email)) {
                        toast.error("Invalid email format.");
                      }
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                  />
                </Form.Group>
                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={mutation.isPending}
                  >
                    {mutation.isPending
                      ? "Saving..."
                      : editId
                      ? "Update Coater"
                      : "Add Coater"}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </div>
  );
}
